import { useEffect, useState, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { adminGet, adminPost } from './adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };
const BLANK = { imei: '', manufacturer_batch: '', date_received: '', device_type: 'HUB', iccid: '', msisdn: '', operator: 'Airtel M2M' };

// Phase 7 "Receive devices": batch receipt from the manufacturer (was the register panel on Device inventory) + CSV import.
export default function ReceiveDevicesPage() {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [received, setReceived] = useState([]);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const { role } = useOutletContext() || {};
  const canImport = !role || role === 'platform_admin';

  useEffect(() => { adminGet('/device-types').then(setTypes).catch(() => {}); }, []);

  const nextIdPreview = () => { const t = types.find(x => x.code === form.device_type); return t ? `TRK-${t.id_prefix}-······` : '—'; };

  const importCsv = async (file) => {
    try {
      const res = await adminPost('/devices/import-csv', { csv: await file.text() });
      toast.success(`Imported ${res.created} devices${res.skipped ? `, skipped ${res.skipped}` : ''}`);
      (res.skipped_rows || []).slice(0, 3).forEach(s => toast.warning(`Row ${s.row} (${s.imei}): ${s.reason}`));
      setReceived(r => [...(res.devices || []).map(d => ({ trackotic_id: d.trackotic_id, imei: d.imei })), ...r]);
    } catch (e) { toast.error(e.message); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (sendToQC) => {
    if (form.imei.length !== 15) { toast.error('IMEI must be 15 digits'); return; }
    if (sendToQC && (form.iccid.length < 19 || form.iccid.length > 20)) { toast.error('ICCID must be 19–20 digits'); return; }
    setSaving(true);
    try {
      const body = { imei: form.imei, manufacturer_batch: form.manufacturer_batch || null, date_received: form.date_received || null, device_type: form.device_type };
      if (sendToQC) { body.iccid = form.iccid; body.msisdn = form.msisdn; body.operator = form.operator; }
      const res = await adminPost('/devices/register', body);
      toast.success(res.trackotic_id ? `Received · ${res.trackotic_id}` : 'Received (draft, no SIM yet)');
      setReceived(r => [{ trackotic_id: res.trackotic_id || '— (draft)', imei: form.imei, id: res.id }, ...r]);
      setForm({ ...BLANK, device_type: form.device_type, manufacturer_batch: form.manufacturer_batch, date_received: form.date_received, operator: form.operator });
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const field = (label, key, props = {}) => (
    <div>
      <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>{label}</label>
      <Input data-testid={`register-${key}-input`} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} className="bg-white border-[#D9D6CD]" {...props} />
    </div>
  );

  return (
    <div className="p-8 max-w-6xl" data-testid="receive-devices-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Receive devices</h1>
          <p className="text-sm mt-1" style={{ color: '#6b7069' }}>Batch receipt from the manufacturer. Each device gets its permanent Track0tic ID on save.</p>
        </div>
        <div className="flex gap-3">
          {canImport && <>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="csv-file-input" onChange={e => e.target.files?.[0] && importCsv(e.target.files[0])} />
            <Button variant="outline" data-testid="import-csv-btn" onClick={() => fileRef.current?.click()} className="bg-white border-[#D9D6CD]">Import CSV</Button>
          </>}
          <Button variant="outline" data-testid="goto-inventory-btn" onClick={() => navigate('/admin/devices')} className="bg-white border-[#D9D6CD]">Open inventory</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-lg border bg-white p-6" style={{ borderColor: '#E5E3DC' }} data-testid="register-device-panel">
          <p className="text-xs font-bold tracking-wide mb-3" style={{ color: '#0B6B55' }}>STEP 1 · FROM MANUFACTURER</p>
          <div className="space-y-3 mb-6">
            {field('Modem IMEI (scan barcode)', 'imei', { placeholder: '15 digits', maxLength: 15 })}
            <div className="grid grid-cols-2 gap-3">
              {field('Manufacturer batch', 'manufacturer_batch', { placeholder: '[Batch no.]' })}
              {field('Date received', 'date_received', { type: 'date' })}
            </div>
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Device type</label>
              <select data-testid="register-device-type-select" value={form.device_type} onChange={e => setForm({ ...form, device_type: e.target.value })}
                className="w-full h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                {types.map(t => <option key={t.code} value={t.code}>{t.display_name}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs font-bold tracking-wide mb-3" style={{ color: '#0B6B55' }}>STEP 2 · AT ASSEMBLY CENTRE (optional now)</p>
          <div className="space-y-3 mb-5">
            {field('SIM ICCID', 'iccid', { placeholder: '19–20 digits', maxLength: 20 })}
            <div className="grid grid-cols-2 gap-3">
              {field('M2M MSISDN', 'msisdn', { placeholder: '[M2M no.]' })}
              <div>
                <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Operator</label>
                <select data-testid="register-operator-select" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })}
                  className="w-full h-9 px-3 rounded-md border bg-white text-sm" style={{ borderColor: '#D9D6CD' }}>
                  {['Airtel M2M', 'Vodafone IoT', 'Jio M2M', 'BSNL M2M'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="rounded-md p-4 mb-6" style={{ background: '#FAF9F6', border: '1px solid #EDEBE4' }}>
            <p className="text-xs" style={{ color: '#6b7069' }}>Track0tic ID (auto-generated on save)</p>
            <p className="text-lg font-bold" style={mono} data-testid="trk-id-preview">{nextIdPreview()}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" disabled={saving} data-testid="save-draft-btn" onClick={() => submit(false)} className="bg-white border-[#D9D6CD]">Save draft</Button>
            <Button disabled={saving} data-testid="save-send-qc-btn" onClick={() => submit(true)} className="flex-1 text-white hover:opacity-90" style={{ background: '#0B6B55' }}>Save and send to QC</Button>
          </div>
        </div>
        <div className="rounded-lg border bg-white p-6" style={{ borderColor: '#E5E3DC' }} data-testid="received-this-session">
          <h2 className="text-lg font-bold mb-3" style={{ color: '#1B1D1C' }}>Received this session <span className="text-sm font-normal" style={{ color: '#9a9e96' }}>· {received.length}</span></h2>
          {received.length === 0 ? <p className="text-sm" style={{ color: '#9a9e96' }}>Scan the first IMEI to start the batch. Labels are printed under Devices → Device labels.</p> : (
            <table className="w-full text-sm"><tbody>
              {received.map((d, i) => <tr key={`${d.imei}-${i}`} className="border-b" style={{ borderColor: '#F3F1EA' }}><td className="py-2" style={mono}>{d.trackotic_id}</td><td style={{ ...mono, color: '#6b7069' }}>{d.imei}</td></tr>)}
            </tbody></table>
          )}
        </div>
      </div>
    </div>
  );
}
